import React, { useRef, useState } from "react";
import CreateTheoryQuestion from "../../pages/CreateTheoryQuestionAI";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Pagination,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  FaPlus,
  FaCheck,
  FaEdit,
  FaTrash,
  FaPen,
  FaStar,
  FaRobot,
  FaUser,
  FaMagic,
  FaImage,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
} from "react-icons/fa";
import DeleteDialog from "../dialog/delete";

const ITEMS_PER_PAGE = 5; // 👈 change per your need

const TheorySection = ({
  subId,
  tokenString,
  subjectName,
  numThryQuestions,
  setNumThryQuestions,
  showTheoryGen,
  toggleTheory,
  handleAutoExamTheory,
  loadingautothry,
  handleTheorySubmit,
  questionImagePreview,
  answerImagePreview,
  editingTheory,
  isRemovingBg,
  TQmode,
  setTQMode,
  TAmode,
  setTAMode,
  theoryForm,
  setTheoryForm,
  TheoryQuestionRef,
  TheoryAnswerRef,
  questionImageInputRef,
  answerImageInputRef,
  handleQuestionImageUpload,
  handleAnswerImageUpload,
  loadingthry,
  instTheories,
  expandedTheoryId,
  setExpandedTheoryId,
  editDisabledId,
  setEditDisabledId,
  renderContent,
  setEditingTheory,
  deleteOpen,
  setDeleteOpen,
  confirmDelete,
  setDeleteId,
  setDeleteType,
  setInstTheories,
}) => {
  const [page, setPage] = useState(1);
  const editFormRef = useRef(null);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // 🍀 Calculate pagination
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedItems = instTheories.slice(startIndex, endIndex);


  const containsMath = (text = "") => {
    const mathPatterns = [
      "\\frac",
      "\\sqrt",
      "\\sum",
      "\\int",
      "^",
      "_",
      "\\(",
      "\\)",
    ];

    return mathPatterns.some((pattern) => text.includes(pattern));
  };

return (
  <Box>
    {/* ── Top Controls ───────────────────────────────────────────────────── */}
    <Stack
      direction={"column"}
      alignItems={"flex-start"}
      justifyContent="space-between"
      mb={3}
      gap={1.5}
    >
      <Button
        onClick={toggleTheory}
        variant="contained"
        startIcon={showTheoryGen ? <FaUser size={12} /> : <FaRobot size={12} />}
        sx={{
          borderRadius: 99,
          textTransform: "none",
          fontWeight: 700,
          fontSize: "0.78rem",
          px: 2.5,
          background: showTheoryGen
            ? "linear-gradient(135deg, #7c3aed, #a855f7)"
            : "linear-gradient(135deg, #0288D1, #0ea5e9)",
          boxShadow: showTheoryGen
            ? "0 4px 12px rgba(124,58,237,0.3)"
            : "0 4px 12px rgba(2,136,209,0.3)",
          "&:hover": {
            background: showTheoryGen
              ? "linear-gradient(135deg, #6d28d9, #7c3aed)"
              : "linear-gradient(135deg, #0277bd, #0288D1)",
          },
        }}
      >
        {showTheoryGen ? "Switch to Manual Mode" : "Switch to AI Mode"}
      </Button>
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, color: "#0c1a2e", letterSpacing: "-0.3px" }}
        >
          Theory Questions
        </Typography>
        <Typography variant="caption" sx={{ color: "#64748b" }}>
          {instTheories.length} question{instTheories.length !== 1 ? "s" : ""}{" "}
          added
        </Typography>
      </Box>
    </Stack>

    {/* ── Auto Generate Row ──────────────────────────────────────────────── */}
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 2.5,
        background: "rgba(2,136,209,0.04)",
        border: "1px solid rgba(2,136,209,0.14)",
      }}
    >
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#0288D1",
          textTransform: "uppercase",
          letterSpacing: "0.8px",
          mb: 1.5,
        }}
      >
        🤖 Auto Generate
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
      >
        <TextField
          type="number"
          placeholder="No. of questions"
          value={numThryQuestions}
          onChange={(e) => setNumThryQuestions(Number(e.target.value))}
          size="small"
          sx={{
            width: { xs: "100%", sm: 200 },
            "& .MuiOutlinedInput-root": {
              color: "#0c1a2e",
              borderRadius: 2,
              background: "#fff",
              "& fieldset": { borderColor: "rgba(2,136,209,0.25)" },
              "&:hover fieldset": { borderColor: "#0288D1" },
              "&.Mui-focused fieldset": { borderColor: "#0288D1" },
            },
            "& input::placeholder": { color: "#94a3b8", fontSize: "0.85rem" },
          }}
        />
        <Button
          onClick={handleAutoExamTheory}
          disabled={loadingautothry || !numThryQuestions}
          variant="contained"
          startIcon={loadingautothry ? null : <FaMagic size={12} />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            background:
              loadingautothry || !numThryQuestions
                ? "#e2e8f0"
                : "linear-gradient(135deg, #0288D1, #0ea5e9)",
            color: loadingautothry || !numThryQuestions ? "#94a3b8" : "#fff",
            boxShadow:
              loadingautothry || !numThryQuestions
                ? "none"
                : "0 4px 12px rgba(2,136,209,0.25)",
            "&:hover": {
              background:
                loadingautothry || !numThryQuestions
                  ? "#e2e8f0"
                  : "linear-gradient(135deg, #0277bd, #0288D1)",
            },
            "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
          }}
        >
          {loadingautothry ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
              <span>Generating…</span>
            </Stack>
          ) : (
            "Auto Generate"
          )}
        </Button>
      </Stack>
    </Paper>

    {/* ── AI Mode ────────────────────────────────────────────────────────── */}
    {showTheoryGen && (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2.5,
          background: "rgba(124,58,237,0.04)",
          border: "1px solid rgba(124,58,237,0.16)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid rgba(124,58,237,0.1)",
            background: "rgba(124,58,237,0.06)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FaRobot size={14} color="#7c3aed" />
            <Typography
              sx={{ fontWeight: 700, color: "#7c3aed", fontSize: "0.82rem" }}
            >
              AI Theory Generator
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <CreateTheoryQuestion
            token={tokenString}
            subjectId={subId}
            subjectName={subjectName}
            setInstTheories={setInstTheories}
          />
        </Box>
      </Paper>
    )}

    {/* ── Manual Form ────────────────────────────────────────────────────── */}
    {!showTheoryGen && (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2.5,
          background: "#fff",
          border: "1px solid rgba(2,136,209,0.15)",
          boxShadow: "0 2px 12px rgba(2,136,209,0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid #e2e8f0",
            background:
              "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.02))",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FaPen size={13} color="#0288D1" />
            <Typography
              sx={{ fontWeight: 700, color: "#0c1a2e", fontSize: "0.88rem" }}
            >
              {editingTheory ? "Edit Theory Question" : "Add Theory Question"}
            </Typography>
          </Stack>
        </Box>

        <Box
          component="form"
          onSubmit={handleTheorySubmit}
          ref={editFormRef}
          sx={{ p: 2.5 }}
        >
          <Stack spacing={2.5}>
            {/* ── Question Section ─────────────────────────────────────── */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "#334155",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  mb: 1,
                }}
              >
                Question
              </Typography>

              {/* Question image preview */}
              {(questionImagePreview || editingTheory?.question_image) && (
                <Box
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  {isRemovingBg && (
                    <Stack alignItems="center" spacing={1} py={2}>
                      <CircularProgress size={18} sx={{ color: "#0288D1" }} />
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Removing background…
                      </Typography>
                    </Stack>
                  )}
                  <img
                    src={questionImagePreview || editingTheory?.question_image}
                    alt="Question preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 180,
                      borderRadius: 8,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                  {TQmode === "text" && (
                    <textarea
                      ref={TheoryQuestionRef}
                      placeholder="Type your question here…"
                      value={theoryForm.Question}
                      onChange={(e) =>
                        setTheoryForm({
                          ...theoryForm,
                          Question: e.target.value,
                        })
                      }
                      style={{
                        width: "100%",
                        minHeight: 120,
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1.5px solid rgba(2,136,209,0.25)",
                        fontSize: 15,
                        fontFamily: "inherit",
                        color: "#0c1a2e",
                        background: "#fff",
                        resize: "vertical",
                        outline: "none",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#0288D1")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(2,136,209,0.25)")
                      }
                    />
                  )}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setTQMode("text")}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    border: "1px solid rgba(2,136,209,0.2)",
                    borderRadius: 1.5,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    flexShrink: 0,
                    background:
                      TQmode === "text"
                        ? "linear-gradient(135deg, #0288D1, #0ea5e9)"
                        : "#f8fafc",
                    color: TQmode === "text" ? "#fff" : "#64748b",
                    transition: "all 0.2s",
                  }}
                >
                  Text
                </Box>
              </Stack>

              {/* Question image upload */}
              <Box
                component="label"
                sx={{
                  mt: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  border: "1.5px dashed rgba(2,136,209,0.3)",
                  background: "rgba(2,136,209,0.02)",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#0288D1",
                    background: "rgba(2,136,209,0.06)",
                  },
                }}
              >
                <FaImage size={13} color="#0288D1" />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#0288D1",
                  }}
                >
                  Attach question image (optional)
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  ref={questionImageInputRef}
                  onChange={handleQuestionImageUpload}
                  style={{ display: "none" }}
                />
              </Box>
            </Box>

            <Divider sx={{ borderColor: "#f1f5f9" }} />

            {/* ── Answer Section ───────────────────────────────────────── */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "#334155",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  mb: 1,
                }}
              >
                Answer
              </Typography>

              {/* Answer image preview */}
              {(answerImagePreview || editingTheory?.answer_image) && (
                <Box
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  {isRemovingBg && (
                    <Stack alignItems="center" spacing={1} py={2}>
                      <CircularProgress size={18} sx={{ color: "#0288D1" }} />
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Removing background…
                      </Typography>
                    </Stack>
                  )}
                  <img
                    src={answerImagePreview || editingTheory?.answer_image}
                    alt="Answer preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 180,
                      borderRadius: 8,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                  {TAmode === "text" && (
                    <textarea
                      ref={TheoryAnswerRef}
                      placeholder="Type your answer here…"
                      value={theoryForm.answer}
                      onChange={(e) =>
                        setTheoryForm({ ...theoryForm, answer: e.target.value })
                      }
                      style={{
                        width: "100%",
                        minHeight: 120,
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1.5px solid rgba(2,136,209,0.25)",
                        fontSize: 15,
                        fontFamily: "inherit",
                        color: "#0c1a2e",
                        background: "#fff",
                        resize: "vertical",
                        outline: "none",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#0288D1")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(2,136,209,0.25)")
                      }
                    />
                  )}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setTAMode("text")}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    border: "1px solid rgba(2,136,209,0.2)",
                    borderRadius: 1.5,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    flexShrink: 0,
                    background:
                      TAmode === "text"
                        ? "linear-gradient(135deg, #0288D1, #0ea5e9)"
                        : "#f8fafc",
                    color: TAmode === "text" ? "#fff" : "#64748b",
                    transition: "all 0.2s",
                  }}
                >
                  Text
                </Box>
              </Stack>

              {/* Answer image upload */}
              <Box
                component="label"
                sx={{
                  mt: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  border: "1.5px dashed rgba(5,150,105,0.3)",
                  background: "rgba(5,150,105,0.02)",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#059669",
                    background: "rgba(5,150,105,0.06)",
                  },
                }}
              >
                <FaImage size={13} color="#059669" />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#059669",
                  }}
                >
                  Attach answer image (optional)
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  ref={answerImageInputRef}
                  onChange={handleAnswerImageUpload}
                  style={{ display: "none" }}
                />
              </Box>
            </Box>

            <Divider sx={{ borderColor: "#f1f5f9" }} />

            {/* ── Total Marks ──────────────────────────────────────────── */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "#334155",
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  mb: 0.75,
                }}
              >
                Total Marks
              </Typography>
              <TextField
                type="number"
                placeholder="e.g. 10"
                value={theoryForm.marks}
                onChange={(e) =>
                  setTheoryForm({ ...theoryForm, marks: e.target.value })
                }
                required
                size="small"
                sx={{
                  width: 160,
                  "& .MuiOutlinedInput-root": {
                    color: "#0c1a2e",
                    borderRadius: 2,
                    background: "#fff",
                    "& fieldset": { borderColor: "rgba(2,136,209,0.25)" },
                    "&:hover fieldset": { borderColor: "#0288D1" },
                    "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                  },
                  "& input::placeholder": {
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaStar size={12} color="#0288D1" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* ── Submit ───────────────────────────────────────────────── */}
            <Button
              type="submit"
              variant="contained"
              disabled={loadingthry}
              startIcon={
                loadingthry ? null : editingTheory ? (
                  <FaCheck size={12} />
                ) : (
                  <FaPlus size={12} />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                alignSelf: "flex-start",
                px: 3,
                background: "linear-gradient(135deg, #059669, #10b981)",
                boxShadow: "0 4px 12px rgba(5,150,105,0.25)",
                "&:hover": {
                  background: "linear-gradient(135deg, #047857, #059669)",
                },
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {loadingthry ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                  <span>{editingTheory ? "Updating…" : "Saving…"}</span>
                </Stack>
              ) : editingTheory ? (
                "Update Question"
              ) : (
                "Save Question"
              )}
            </Button>
          </Stack>
        </Box>
      </Paper>
    )}

    {/* ── Theory Questions List ───────────────────────────────────────────── */}
    <Stack spacing={1.5}>
      {paginatedItems.map((item, i) => {
        const isExpanded = expandedTheoryId === item.id;
        const isEditingItem = editDisabledId === item.id;
        const hasCalculation = containsMath(item.Question);

        return (
          <Paper
            key={item.id}
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: isEditingItem
                ? "1px solid rgba(2,136,209,0.4)"
                : "1px solid #e2e8f0",
              background: isEditingItem ? "rgba(2,136,209,0.02)" : "#f8fafc",
              overflow: "hidden",
              transition: "all 0.2s",
              boxShadow: isEditingItem
                ? "0 4px 16px rgba(2,136,209,0.1)"
                : "none",
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Question image or SVG */}
              {item.question_image ? (
                <Box
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  <img
                    src={item.question_image}
                    alt="Question"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 180,
                      objectFit: "contain",
                      borderRadius: 6,
                    }}
                  />
                </Box>
              ) : item.question_svg ? (
                <Box
                  sx={{ mb: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: item.question_svg }}
                />
              ) : null}

              {/* Question text */}
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.2,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#fff" }}
                  >
                    {startIndex + i + 1}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    color: "#0c1a2e",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                  }}
                >
                  {renderContent(item.Question)}
                </Box>
              </Stack>

              {/* Expanded answer */}
              {isExpanded && (
                <Box
                  sx={{
                    mt: 2,
                    pl: 5,
                    pt: 2,
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      mb: 1,
                    }}
                  >
                    Answer
                  </Typography>

                  {item.answer_image ? (
                    <Box
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                        background: "#fff",
                        p: 1,
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={item.answer_image}
                        alt="Answer"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 180,
                          objectFit: "contain",
                          borderRadius: 6,
                        }}
                      />
                    </Box>
                  ) : item.answer_svg ? (
                    <Box
                      sx={{ mb: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: item.answer_svg }}
                    />
                  ) : null}

                  <Box
                    sx={{
                      color: "#334155",
                      fontSize: "0.88rem",
                      lineHeight: 1.7,
                      mb: 1.5,
                    }}
                  >
                    {renderContent(item.answer)}
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Marks:
                    </Typography>
                    <Chip
                      label={item.marks}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        background: "rgba(5,150,105,0.12)",
                        color: "#059669",
                        border: "1px solid rgba(5,150,105,0.25)",
                      }}
                    />
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                px: 2,
                py: 1.2,
                borderTop: "1px solid #e2e8f0",
                background: "#fff",
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                gap={0.5}
              >
                <Button
                  size="small"
                  onClick={() =>
                    setExpandedTheoryId(isExpanded ? null : item.id)
                  }
                  startIcon={
                    isExpanded ? (
                      <FaChevronUp size={10} />
                    ) : (
                      <FaChevronDown size={10} />
                    )
                  }
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    color: "#0288D1",
                    border: "1px solid rgba(2,136,209,0.25)",
                    px: 1.5,
                    "&:hover": { background: "rgba(2,136,209,0.06)" },
                  }}
                >
                  {isExpanded ? "See less" : "See more"}
                </Button>

                <Tooltip
                  title={
                    hasCalculation
                      ? "Editing disabled — contains math notation"
                      : "Edit question"
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={isEditingItem || hasCalculation}
                      onClick={() => {
                        if (hasCalculation) return;
                        setEditDisabledId(item.id);
                        setEditingTheory(item);
                        setTheoryForm(item);
                        if (showTheoryGen) toggleTheory();
                        setTimeout(() => {
                          editFormRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }, 200);
                      }}
                      sx={{
                        color: hasCalculation ? "#cbd5e1" : "#0288D1",
                        background: hasCalculation
                          ? "#f8fafc"
                          : "rgba(2,136,209,0.08)",
                        border: `1px solid ${
                          hasCalculation ? "#e2e8f0" : "rgba(2,136,209,0.2)"
                        }`,
                        borderRadius: 1.5,
                        "&:hover": {
                          background: hasCalculation
                            ? "#f8fafc"
                            : "rgba(2,136,209,0.16)",
                        },
                        "&:disabled": { opacity: 0.4 },
                      }}
                    >
                      {isEditingItem ? (
                        <CircularProgress size={12} sx={{ color: "#0288D1" }} />
                      ) : (
                        <FaEdit size={13} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Delete question" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeleteId(item.id);
                      setDeleteType("insttheory");
                      setDeleteOpen(true);
                    }}
                    sx={{
                      color: "#ef4444",
                      background: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.2)",
                      borderRadius: 1.5,
                      "&:hover": { background: "rgba(239,68,68,0.16)" },
                    }}
                  >
                    <FaTrash size={13} />
                  </IconButton>
                </Tooltip>

                {hasCalculation && (
                  <Chip
                    label="Math — Edit Disabled"
                    size="small"
                    icon={
                      <FaExclamationTriangle
                        size={9}
                        style={{ marginLeft: 8, color: "#f59e0b" }}
                      />
                    }
                    sx={{
                      height: 20,
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      background: "rgba(245,158,11,0.1)",
                      color: "#b45309",
                      border: "1px solid rgba(245,158,11,0.25)",
                    }}
                  />
                )}

                {isEditingItem && (
                  <Chip
                    label="Currently Editing"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      background: "rgba(2,136,209,0.1)",
                      color: "#0288D1",
                      border: "1px solid rgba(2,136,209,0.25)",
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Paper>
        );
      })}
    </Stack>

    {/* ── Pagination ──────────────────────────────────────────────────────── */}
    {instTheories.length > 0 && (
      <Stack alignItems="center" sx={{ mt: 3, mb: 2 }}>
        <Pagination
          count={Math.ceil(instTheories.length / ITEMS_PER_PAGE)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
          size="large"
          sx={{
            "& .MuiPaginationItem-root": {
              fontWeight: 700,
              borderRadius: 2,
              color: "#fff",
            },
            "& .MuiPaginationItem-root.Mui-selected": {
              background: "green",
              color: "#fff",
              boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
            },
          }}
        />
      </Stack>
    )}

    {/* ── Delete Dialog ───────────────────────────────────────────────────── */}
    <DeleteDialog
      open={deleteOpen}
      onClose={() => setDeleteOpen(false)}
      onConfirm={confirmDelete}
      title="question"
    />
  </Box>
);
};

export default TheorySection;